import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Divider,
  Icon,
  Button,
  useColorModeValue,
  Progress,
} from '@chakra-ui/react'
import { LuZap } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PlanType } from '../../types/subscription'
import { useCreditBalance } from '../../api/api_hooks/credits'

interface SubscriptionInfoProps {
  plan?: PlanType | null
  modules?: string[]
  /** False for a collaborateur invited by another pilot — their plan/credits
   * are inherited from that pilot, so changing plan independently is disabled. */
  canChangePlan?: boolean
}


const PLAN_COLORS: Record<PlanType, string> = {
  [PlanType.FREE]: 'gray',
  [PlanType.PRO]: 'teal',
  [PlanType.PREMIUM]: 'orange',
  [PlanType.ENTERPRISE]: 'purple',
  [PlanType.ENTERPRISE_PENDING]: 'yellow',
}

const PLAN_DISPLAY_NAMES: Record<PlanType, string> = {
  [PlanType.FREE]: 'profile.subscription.noPlanName',
  [PlanType.PRO]: 'Essentiel',
  [PlanType.PREMIUM]: 'Premium',
  [PlanType.ENTERPRISE]: 'Institution',
  [PlanType.ENTERPRISE_PENDING]: 'profile.subscription.enterprisePending',
}

export default function SubscriptionInfo({ plan, modules, canChangePlan = true }: SubscriptionInfoProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const { data: creditData } = useCreditBalance()

  // Use data from backend (plan and modules are now stored in database)
  const currentPlan = plan


  const getPlanName = (planType: PlanType) => {
    const planKeys: Record<PlanType, string> = {
      [PlanType.FREE]: 'profile.subscription.plans.free.name',
      [PlanType.PRO]: 'profile.subscription.plans.pro.name',
      [PlanType.PREMIUM]: 'profile.subscription.plans.premium.name',
      [PlanType.ENTERPRISE]: 'profile.subscription.plans.enterprise.name',
      [PlanType.ENTERPRISE_PENDING]: 'profile.subscription.plans.enterprisePending.name',
    }
    return t(planKeys[planType])
  }

  const getPlanDescription = (planType: PlanType) => {
    const planKeys: Record<PlanType, string> = {
      [PlanType.FREE]: 'profile.subscription.plans.free.description',
      [PlanType.PRO]: 'profile.subscription.plans.pro.description',
      [PlanType.PREMIUM]: 'profile.subscription.plans.premium.description',
      [PlanType.ENTERPRISE]: 'profile.subscription.plans.enterprise.description',
      [PlanType.ENTERPRISE_PENDING]: 'profile.subscription.plans.enterprisePending.description',
    }
    return t(planKeys[planType])
  }

  const credits = creditData?.balance

  // Calculate credit percentage and color
  const getCreditColor = () => {
    if (!credits || credits.totalCredits >= 999999) return 'purple'
    const percentage = (credits.remainingCredits / credits.totalCredits) * 100
    if (percentage < 20) return 'red'
    if (percentage < 50) return 'orange'
    return 'green'
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short'
    })
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Plan Card */}
      <Card
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        boxShadow="md"
        overflow="hidden"
      >
        <CardHeader bg="purple.50">
          <HStack justify="space-between">
            <Heading size="md" color="purple.700">{t('profile.subscription.title')}</Heading>
            {currentPlan && currentPlan !== PlanType.FREE && (
              <Badge colorScheme={PLAN_COLORS[currentPlan]} fontSize="md" px={3} py={1} borderRadius="lg">
                {t(PLAN_DISPLAY_NAMES[currentPlan])}
              </Badge>
            )}
          </HStack>
        </CardHeader>
        <Divider />
        <CardBody>
          <VStack align="stretch" spacing={4}>
            {currentPlan ? (
              <>
                <Text color="gray.600">{currentPlan !== PlanType.FREE ? getPlanDescription(currentPlan) : t('profile.subscription.noPlanDetail')}</Text>
                {currentPlan === PlanType.ENTERPRISE_PENDING && (
                  <Badge colorScheme="orange" fontSize="sm">
                    ⏳ {t('profile.subscription.enterprisePending')}
                  </Badge>
                )}
                {canChangePlan ? (
                  <HStack spacing={4} wrap="wrap">
                    <Button
                      size="md"
                      variant="outline"
                      colorScheme="purple"
                      onClick={() => navigate('/change-plan')}
                      fontWeight={600}
                      borderRadius="xl"
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'lg',
                      }}
                      transition="all 0.2s"
                    >
                      {t('profile.subscription.changePlan')}
                    </Button>
                  </HStack>
                ) : (
                  <Text fontSize="xs" color="gray.500">
                    Votre offre est liée à celle de la personne qui vous a invité(e) — contactez-la pour en changer.
                  </Text>
                )}
              </>
            ) : (
              <VStack align="stretch" spacing={3}>
                <Text color="gray.600">{t('profile.subscription.noPlan')}</Text>
                <Button
                  colorScheme="purple"
                  onClick={() => navigate('/select-plan')}
                  alignSelf="flex-start"
                  size="md"
                  fontWeight={600}
                  borderRadius="xl"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg',
                  }}
                  transition="all 0.2s"
                >
                  {t('profile.subscription.selectPlan')}
                </Button>
              </VStack>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Credits Card */}
      {credits && (
        <Card
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="2xl"
          boxShadow="md"
          overflow="hidden"
        >
          <CardHeader bg={`${getCreditColor()}.50`}>
            <HStack justify="space-between">
              <HStack spacing={3}>
                <Icon as={LuZap} boxSize={6} color={`${getCreditColor()}.600`} />
                <Heading size="md" color={`${getCreditColor()}.700`}>
                  {t('profile.credits.title')}
                </Heading>
              </HStack>
              {credits.totalCredits < 999999 && credits.remainingCredits < credits.totalCredits * 0.2 && (
                <Badge colorScheme="red" fontSize="sm" px={3} py={1} borderRadius="lg">
                  ⚠️ {t('profile.credits.low')}
                </Badge>
              )}
            </HStack>
          </CardHeader>
          <Divider />
          <CardBody>
            <VStack align="stretch" spacing={4}>
              {credits.totalCredits >= 999999 ? (
                <Box>
                  <Text fontSize="3xl" fontWeight="bold" color="purple.600" mb={2}>
                    ∞ {t('profile.credits.unlimited')}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {t('profile.credits.unlimitedDescription', { plan: currentPlan ? getPlanName(currentPlan) : '' })}
                  </Text>
                </Box>
              ) : (
                <>
                  <HStack justify="space-between" align="baseline">
                    <Box>
                      <Text fontSize="sm" color="gray.600" mb={1}>
                        {t('profile.credits.remaining')}
                      </Text>
                      <HStack spacing={2} align="baseline">
                        <Text fontSize="3xl" fontWeight="bold" color={`${getCreditColor()}.600`}>
                          {credits.remainingCredits}
                        </Text>
                        <Text fontSize="lg" color="gray.500">
                          / {credits.totalCredits}
                        </Text>
                      </HStack>
                    </Box>
                    <Box textAlign="right">
                      <Text fontSize="sm" color="gray.600" mb={1}>
                        {t('profile.credits.used')}
                      </Text>
                      <Text fontSize="2xl" fontWeight="semibold" color="gray.700">
                        {credits.usedCredits}
                      </Text>
                    </Box>
                  </HStack>
                  <Progress
                    value={(credits.remainingCredits / credits.totalCredits) * 100}
                    size="md"
                    colorScheme={getCreditColor()}
                    borderRadius="full"
                  />
                  <HStack justify="space-between" fontSize="xs" color="gray.500">
                    <Text>
                      {t('profile.credits.period')}: {formatDate(credits.periodStartDate)}
                    </Text>
                    <Text>
                      {t('profile.credits.renewal')}: {formatDate(credits.periodEndDate)}
                    </Text>
                  </HStack>
                  {credits.remainingCredits === 0 && (
                    <Badge colorScheme="red" p={2} borderRadius="md" fontSize="sm">
                      ⚠️ {t('profile.credits.exhausted')}
                    </Badge>
                  )}
                </>
              )}
              {canChangePlan && (
                <Button
                  size="md"
                  colorScheme="purple"
                  onClick={() => navigate('/change-plan')}
                  fontWeight={600}
                  borderRadius="xl"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg',
                  }}
                  transition="all 0.2s"
                >
                  {credits.totalCredits >= 999999 ? t('profile.credits.manageSubscription') : t('profile.credits.getMore')}
                </Button>
              )}
            </VStack>
          </CardBody>
        </Card>
      )}

    </VStack>
  )
}
